import { withPracticeAnalytics } from '@/utils/practice-payload';
import { ScrollShadow, Skeleton } from '@nextui-org/react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useCreateOrUpdatePractice } from 'Src/hooks/queries/practices';
import { useAuthStore } from 'Src/store/use-auth-store';
import { useMediaQuery } from 'usehooks-ts';
import { getGradientForIndexAsString } from '../../../utils/colors';
import ActionTile from './action-tile';

interface Task {
  id: number;
  name: string;
  description: string;
}

export interface DisplayScenariosProps {
  tasks: Task[];
  isLoading?: boolean;
  tag: string;
  selectedCohortIds: number[];
}

const DisplayScenarios = ({
  tasks,
  isLoading,
  tag,
  selectedCohortIds,
}: DisplayScenariosProps) => {
  const navigate = useNavigate();
  const createPracticeMutation = useCreateOrUpdatePractice();
  const orgId = useAuthStore((state) => state.orgId);
  const [loadingPracticeId, setLoadingPracticeId] = useState<number | null>(
    null
  );
  const [searchQuery] = useState('');
  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleScenarioClick = async (task: Task) => {
    try {
      setLoadingPracticeId(task.id);
      const response = await createPracticeMutation.mutateAsync(
        withPracticeAnalytics(
          {
            name: task.name,
            org_id: orgId,
          },
          selectedCohortIds
        )
      );

      if (response && typeof response === 'object' && 'id' in response) {
        navigate(
          `${response.id}/run/?action=${encodeURIComponent(task.name)}&id=${response.id}&tag=${encodeURIComponent(tag)}`
        );
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      toast.error('Failed to create practice session');
      console.error(error);
    } finally {
      setLoadingPracticeId(null);
    }
  };

  const filteredScenarios = tasks.filter((task) =>
    task.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const skeletonCount = isMobile ? 4 : 6;

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-col w-full">
        <ScrollShadow
          size={12}
          offset={80}
          orientation="vertical"
          className="w-full h-auto max-h-[60vh] md:max-h-[500px] overflow-y-auto hide-scrollbar"
        >
          {isLoading ? (
            <div
              className={`py-2 ${
                isMobile
                  ? 'grid grid-cols-2 gap-4 w-full pb-24'
                  : 'flex gap-4 flex-wrap'
              }`}
            >
              {Array.from({ length: skeletonCount }).map((_, index) => (
                <Skeleton
                  key={`scenario-skeleton-${index}`}
                  className="w-full md:w-[200px] aspect-square md:h-[200px] shrink-0 min-w-0 rounded-2xl border-0 transition-shadow hover:shadow-lg"
                />
              ))}
            </div>
          ) : (
            <div
              className={`py-2 ${
                isMobile
                  ? 'grid grid-cols-2 gap-4 w-full pb-24'
                  : 'flex gap-4 flex-wrap'
              }`}
            >
              {filteredScenarios.map((task, index) => {
                const bgColor = getGradientForIndexAsString(
                  tag,
                  index,
                  true,
                  0.75
                );
                const hoverBgColor = getGradientForIndexAsString(
                  tag,
                  index,
                  true,
                  0.85
                );
                return (
                  <ActionTile
                    key={task.id}
                    title={task.name}
                    bgColor={bgColor}
                    hoverBgColor={hoverBgColor}
                    onAction={() => handleScenarioClick(task)}
                    isLoading={loadingPracticeId === task.id}
                    className={isMobile ? 'w-full min-w-0' : ''}
                  />
                );
              })}
            </div>
          )}
          {!isLoading && tasks.length === 0 && (
            <div className="w-full text-center flex items-center justify-center py-20">
              <p className="text-gray-500">No scenarios available</p>
            </div>
          )}
        </ScrollShadow>
      </div>
    </div>
  );
};

export default DisplayScenarios;
